import React from 'react';
import { useLocation } from '@tanstack/react-router';
import logo from '../../assets/logo.svg';
import { Header } from './Header/Header';
import { paths } from '@/constants';

interface PageContainerProps {
  children: React.ReactNode;
  hideLogo?: boolean;
  hideHeader?: boolean;
  authLayer?: boolean;
}

export function PageContainer({
  children,
  hideLogo,
  hideHeader,
  authLayer,
}: PageContainerProps) {
  const location = useLocation();

  // Auth-related routes where header should be hidden
  const authRoutes = [paths.auth, paths.oauthCallback];
  const isAuthRoute = authRoutes.some((route) =>
    location.pathname.startsWith(route)
  );

  // Determine if we should show the header (not on auth routes unless explicitly overridden)
  const shouldShowHeader = !hideHeader && !isAuthRoute;
  const isLandingRoute = /^\/([a-z]{2}\/?)?$/.test(location.pathname);

  return (
    <div className='relative h-screen bg-[radial-gradient(circle_at_100%_0%,#fff1f3_0%,transparent_45%),radial-gradient(circle_at_0%_100%,#fff1f3_0%,transparent_45%),linear-gradient(to_bottom,var(--color-background)_0%,#fff_100%)] flex flex-col overflow-hidden'>
      {/* Background Layer - Fixed positioning to prevent scroll interference */}
      <div className='fixed inset-0 overflow-hidden pointer-events-none z-0'>
        {authLayer ? (
          // Auth layer with background image and glassmorphic overlay
          <>
            <div
              className='absolute inset-0 bg-cover bg-center bg-no-repeat'
              style={{ backgroundImage: 'url(/images/auth-layer.png)' }}
            />
            <div className='absolute inset-0 backdrop-blur-sm bg-white/30' />
          </>
        ) : (
          // Original decorative circles
          <>
            <div className='absolute top-[-200px] left-[-200px] w-[450px] h-[450px] md:w-[500px] md:h-[500px] lg:w-[600px] lg:h-[600px] rounded-full bg-[radial-gradient(circle,#ffd9de_40%,#fe5066_100%)] opacity-70' />
            <div className='absolute top-1/2 left-1/4 sm:left-[29%] w-[100px] h-[100px] sm:w-[150px] sm:h-[150px] xl:w-[250px] xl:h-[250px] rounded-full bg-[radial-gradient(circle,var(--color-background)_0%,#ff6b7a_50%,#fe324b_100%)] opacity-80' />
            <div className='absolute bottom-[-200px] right-[-200px] w-[400px] h-[400px] md:w-[550px] md:h-[550px] lg:w-[700px] lg:h-[700px] 2xl:w-[800px] 2xl:h-[800px]  rounded-full bg-[linear-gradient(to_right,#ffc4cb_0%,#fe324b_100%)] opacity-60' />
          </>
        )}
      </div>

      {/* Header */}
      {shouldShowHeader && <Header showLandingNavigation={isLandingRoute} />}

      <div
        data-page-scroll-container
        className={`w-full px-2 pt-6 pb-0 sm:px-4 md:px-6 md:pt-8 2xl:px-16 lg:pt-10 overflow-y-auto overflow-x-hidden relative z-10 ${isLandingRoute ? 'flex flex-col' : 'md:pb-8 lg:pb-10'} ${shouldShowHeader ? 'flex-1' : ''}`}
      >
        {!hideLogo && !shouldShowHeader ? (
          <div className='mb-6 flex justify-center'>
            <img src={logo} alt='Hopsa' className='h-12 w-auto' />
          </div>
        ) : null}
        {children}
      </div>
    </div>
  );
}
